import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Modal, Tooltip } from "antd";
import HelloSendLogo from "../../assets/images/HelloSendFullLogo.png";
import { ASSIST_MAIL, APP_URL, API_VERSION } from "../../constants/AppConstants";
import axios from "axios";
import "./PackagePricingPage.css";

const CONNECTION_TOOLTIP =
    "A connection is a messaging channel account — e.g. a Twilio account, a RingCentral account, or a WhatsApp number. " +
    "One Twilio account with multiple phone numbers still counts as 1 connection. " +
    "Each distinct provider account counts as 1.";

const IS_PRODUCTION = process.env.REACT_APP_ENV === "production-1";
const CB_SITE = IS_PRODUCTION ? "oapps" : "oapps-test";

const CB_ITEMS = {
    Starter:  { Monthly: "packaged-price-starter-plan-USD-Monthly",  Yearly: "packaged-price-starter-plan-USD-Yearly"  },
    Growth:   { Monthly: "packaged-price-growth-plan-USD-Monthly",   Yearly: "packaged-price-growth-plan-USD-Yearly"   },
    Business: { Monthly: "packaged-price-business-plan-USD-Monthly", Yearly: "packaged-price-business-plan-USD-Yearly" },
};

const PLAN_TIER = { Starter: 0, Growth: 1, Business: 2 };

const getPlanTierFromCbItem = (cbItemId) => {
    if (!cbItemId) return null;
    return Object.keys(CB_ITEMS).find((planName) =>
        Object.values(CB_ITEMS[planName]).includes(cbItemId)
    ) ?? null;
};

const PLANS = [
    {
        name: "Starter",
        desc: "Perfect for small teams getting started with messaging.",
        priceMonthly: 39,
        priceYearly: 31,
        users: "5 users",
        messages: "2,500 msgs / mo",
        connections: 3,
        featured: false,
        features: [
            "One-on-one messaging",
            "SMS & WhatsApp channels",
            "Shared inbox",
            "Basic automations",
            "Email support",
        ],
    },
    {
        name: "Growth",
        desc: "For growing teams that need more reach and automation.",
        priceMonthly: 99,
        priceYearly: 79,
        users: "25 users",
        messages: "15,000 msgs / mo",
        connections: 10,
        featured: true,
        features: [
            "Everything in Starter",
            "Campaigns & broadcasts",
            "Multi-step automations",
            "WhatsApp Business API",
            "RCS messaging",
            "Email & phone support",
        ],
    },
    {
        name: "Business",
        desc: "Unlimited scale for large teams with full channel coverage.",
        priceMonthly: 299,
        priceYearly: 239,
        users: "Unlimited",
        messages: "75,000 msgs / mo",
        connections: 25,
        featured: false,
        features: [
            "Everything in Growth",
            "Unlimited users",
            "Advanced analytics",
            "Priority support",
            "Custom integrations",
            "Dedicated onboarding",
        ],
    },
];

const COMPARISON_ROWS = [
    { feature: "Users included", starter: "5", growth: "25", business: "Unlimited" },
    { feature: "Messages / month", starter: "2,500", growth: "15,000", business: "75,000" },
    {
        feature: (
            <Tooltip title={CONNECTION_TOOLTIP} placement="right">
                <span className="tp-connection-label">
                    Connections <span className="tp-info-icon">ⓘ</span>
                </span>
            </Tooltip>
        ),
        starter: "3",
        growth: "10",
        business: "25",
    },
    { feature: "One-on-one messaging", starter: true, growth: true, business: true },
    { feature: "SMS channel", starter: true, growth: true, business: true },
    { feature: "WhatsApp Personal", starter: true, growth: true, business: true },
    { feature: "WhatsApp Business API", starter: false, growth: true, business: true },
    { feature: "RCS messaging", starter: false, growth: true, business: true },
    { feature: "Campaigns & broadcasts", starter: false, growth: true, business: true },
    { feature: "Multi-step automations", starter: false, growth: true, business: true },
    { feature: "Advanced analytics", starter: false, growth: false, business: true },
    { feature: "Custom integrations", starter: false, growth: false, business: true },
    { feature: "Dedicated onboarding", starter: false, growth: false, business: true },
    { feature: "Support", starter: "Email", growth: "Email & Phone", business: "Priority" },
];

const getFaqs = (serviceName) => [
    {
        key: "1",
        question: `Does HelloSend for ${serviceName} support two-way communication?`,
        answer:
            `Yes, HelloSend supports two-way messaging, enabling interactive conversations with customers. Your team can receive and respond to SMS and WhatsApp replies directly inside ${serviceName}.`,
    },
    {
        key: "2",
        question: "How does HelloSend help with lead conversion?",
        answer:
            `By using Campaigns and Automations, HelloSend can nurture leads by sending personalized messages, driving engagement and guiding prospects through the sales funnel — all from within ${serviceName}.`,
    },
    {
        key: "3",
        question: "Can HelloSend automate communication processes?",
        answer:
            "Yes, HelloSend allows automated communication such as sending notifications, reminders, or follow-ups based on triggers or scheduled events.",
    },
    {
        key: "4",
        question: "What features are available during the free trial?",
        answer:
            "During the 21-day free trial you have access to the complete range of features. Explore and evaluate the full capabilities before committing to a plan.",
    },
    {
        key: "5",
        question: "What's the difference between annual and monthly billing?",
        answer:
            "Monthly billing is charged on the same day each month. Annual billing involves a single payment for the full year at a discounted rate.",
    },
    {
        key: "6a",
        question: "What counts as a connection?",
        answer:
            "A connection is a single messaging channel account. This can be a Twilio account, a RingCentral account, a WhatsApp number, or any other supported provider. " +
            "If you have one Twilio account with five phone numbers, that counts as 1 connection — not 5. " +
            "Each distinct provider account (regardless of how many numbers are under it) counts as 1 connection. " +
            "You can add extra connections beyond your plan limit at $15 per connection.",
    },
    {
        key: "6",
        question: "Are there any additional costs to consider?",
        answer:
            "Additional connections can be added at $15 each. The AI Addon starts at $25 and is credit-based — credits are purchased upfront and carry over across months, so you never lose what you don't use.",
    },
    {
        key: "7",
        question: "How does the AI Addon credit system work?",
        answer:
            "AI credits are purchased in bundles starting at $25. Credits are consumed as you use AI-powered features and roll over month to month — they don't expire. Usage increases your credit consumption, so you can top up at any time.",
    },
    {
        key: "8",
        question: "Do I get a phone number with my subscription?",
        answer:
            "A number is not included in the subscription. You can purchase one at an additional cost, or bring your own number from Twilio or RingCentral.",
    },
    {
        key: "9",
        question: "What is this subscription for?",
        answer:
            `This subscription provides HelloSend's full messaging platform embedded inside ${serviceName} — enabling SMS, WhatsApp, and RCS conversations without leaving your workflow.`,
    },
    {
        key: "10",
        question: "How can I contact customer support?",
        answer: `For assistance, email us at ${ASSIST_MAIL} and our team will promptly respond to your queries. Phone support is available IST 10am – 12am.`,
    },
];

function CheckIcon({ value }) {
    if (value === true) return <span className="tp-check-yes">✓</span>;
    if (value === false) return <span className="tp-check-no">—</span>;
    return <span>{value}</span>;
}

function FaqItem({ faq, isExpanded, onToggle }) {
    return (
        <div className="tp-faq-item" onClick={onToggle}>
            <div className="tp-faq-row">
                <p className="tp-faq-question">{faq.question}</p>
                <span className="tp-faq-toggle">{isExpanded ? "−" : "+"}</span>
            </div>
            {isExpanded && <p className="tp-faq-answer">{faq.answer}</p>}
        </div>
    );
}

export default function PackagePricingPage() {
    const [searchParams] = useSearchParams();
    const [expandedFaqKey, setExpandedFaqKey] = useState(null);
    const [billing, setBilling] = useState("Monthly");
    const [checkoutStatus, setCheckoutStatus] = useState(null); // null | "success" | "error"
    const [showDowngradeModal, setShowDowngradeModal] = useState(false);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [switchingToPlan, setSwitchingToPlan] = useState(null);
    const [showSwitchConfirmModal, setShowSwitchConfirmModal] = useState(false);
    const [spinButton, setSpinButton] = useState(false);
    let isSuccessCheckout = false;

    const leftServiceId = searchParams.get("leftServiceId");
    const osyncId = searchParams.get("osyncId");
    const leftServiceName = searchParams.get("leftServiceName") || searchParams.get("service");
    const hash = searchParams.get("hash");

    const domain = searchParams.get("domain");
    const rawUrlParams = searchParams.get("urlParams");
    const parsedUrlParams = rawUrlParams ? JSON.parse(decodeURIComponent(rawUrlParams)) : {};
    const companyId = parsedUrlParams?.companyId;
    const userId = parsedUrlParams?.userId;

    const serviceName = leftServiceName
        ? leftServiceName.charAt(0).toUpperCase() + leftServiceName.slice(1)
        : "Service";

    const currentPlanName = getPlanTierFromCbItem(currentPlan);
    const currentPlanTier = currentPlanName != null ? (PLAN_TIER[currentPlanName] ?? -1) : -1;

    useEffect(() => {
        if (!hash) return;
        const headers = { "hs-token": hash };
        if (companyId && userId) headers["hs-orgId"] = `${companyId}_${userId}`;
        if (leftServiceId) headers["hs-serviceId"] = leftServiceId;
        if (osyncId) headers["hs-osyncId"] = osyncId;
        axios
            .get(`${APP_URL}/api/${API_VERSION}/chargebee/license`, { headers, withCredentials: true })
            .then((resp) => {
                const data = resp?.data;
                if (data?.planName && data?.customerId && data?.subscriptionId) {
                    setCurrentPlan(data.planToUpdate ?? data.planName);
                }
            })
            .catch(() => {});
    }, [hash, companyId, userId, leftServiceId, osyncId]);

    const applyChargebeeCustomer = (cbInstance) => {
        const cart = cbInstance?.getCart();
        cart?.setCustomer({
            cf_oapps_id: osyncId,
            cf_leftserviceid: leftServiceId,
            cf_accounttype: "Omni",
            cf_servicename: leftServiceName,
        });
    };

    useEffect(() => {
        const initChargebee = () => {
            window.Chargebee.init({ site: CB_SITE });
            applyChargebeeCustomer(window.Chargebee.getInstance());
            window.Chargebee.registerAgain();
        };

        if (window.Chargebee) {
            applyChargebeeCustomer(window.Chargebee.getInstance());
            window.Chargebee.registerAgain();
            return;
        }

        if (document.querySelector('script[src*="chargebee.js"]')) return;

        const script = document.createElement("script");
        script.src = "https://js.chargebee.com/v2/chargebee.js";
        script.onload = initChargebee;
        document.head.appendChild(script);
    }, []);

    useEffect(() => {
        if (window.Chargebee) {
            applyChargebeeCustomer(window.Chargebee.getInstance());
            window.Chargebee.registerAgain();
        }
    }, [billing]);

    const handleSubscribeClick = async () => {
        if (window.Chargebee !== undefined && window.Chargebee !== null) {
            await window.Chargebee.registerAgain();
        }
        const cbInstance = window?.Chargebee?.getInstance();
        applyChargebeeCustomer(cbInstance);
        cbInstance?.setCheckoutCallbacks(function () {
            return {
                loaded: function () {},
                step: function (value) {
                    if (value === "thankyou_screen") {
                        isSuccessCheckout = true;
                    }
                },
                success: function () {
                    isSuccessCheckout = true;
                },
                close: function () {
                    setCheckoutStatus(isSuccessCheckout ? "success" : "error");
                },
            };
        });
    };

    const handleGoHome = () => {
        const rawUrlParams = searchParams.get("urlParams");
        if (rawUrlParams) {
            const urlParamsInbox = JSON.parse(rawUrlParams);
            delete urlParamsInbox.sideBar;
            delete urlParamsInbox.topBar;
            const queryString = new URLSearchParams(urlParamsInbox).toString();
            window.open(`${window.location.origin}/get-started?${queryString}`, "_self");
        } else {
            const params = new URLSearchParams({
                ...(leftServiceId && { leftServiceId }),
                ...(leftServiceName && { service: leftServiceName }),
                ...(domain && { domain }),
                ...(userId && { userId }),
                ...(companyId && { companyId }),
            });
            window.location.href = `${window.location.origin}/get-started?${params.toString()}`;
        }
    };

    const handleSwitchPlan = () => {
        setSpinButton(true);
        const headers = { "hs-token": hash };
        if (companyId && userId) headers["hs-orgId"] = `${companyId}_${userId}`;
        if (leftServiceId) headers["hs-serviceId"] = leftServiceId;
        if (osyncId) headers["hs-osyncId"] = osyncId;
        axios
            .post(
                `${APP_URL}/api/${API_VERSION}/license/switch/plan?switchToPlan=${encodeURIComponent(switchingToPlan)}`,
                null,
                { headers, withCredentials: true }
            )
            .then((resp) => {
                setSpinButton(false);
                setShowSwitchConfirmModal(false);
                const license = resp?.data?.license;
                if (license?.planName) {
                    setCurrentPlan(license.planToUpdate ?? license.planName);
                }
                setCheckoutStatus("success");
            })
            .catch(() => {
                setSpinButton(false);
                setShowSwitchConfirmModal(false);
                setCheckoutStatus("error");
            });
    };

    const handleToggleFaq = (key) => {
        setExpandedFaqKey((prev) => (prev === key ? null : key));
    };

    return (
        <div className="tp-root">
            {/* Top bar */}
            <div className="tp-top-bar">
                <img src={HelloSendLogo} alt="HelloSend" className="tp-logo" />
            </div>

            {/* Hero */}
            <div className="tp-hero">
                <div className="tp-hero-tag">
                    <span className="tp-hero-tag-dot" />
                    For {serviceName}
                </div>
                <h1 className="tp-hero-title">Simple, transparent pricing</h1>
                <p className="tp-hero-subtitle">
                    Choose the plan that fits your team. Start messaging customers on SMS, WhatsApp &amp; RCS — directly
                    inside {serviceName}.
                </p>
                <p className="tp-trial-note">21-day free trial &nbsp;·&nbsp; No credit card required</p>

                <div className="tp-billing-toggle">
                    <button
                        className={`tp-billing-btn${billing === "Monthly" ? " tp-billing-btn--active" : ""}`}
                        onClick={() => setBilling("Monthly")}
                    >
                        Monthly
                    </button>
                    <button
                        className={`tp-billing-btn${billing === "Yearly" ? " tp-billing-btn--active" : ""}`}
                        onClick={() => setBilling("Yearly")}
                    >
                        Yearly <span className="tp-billing-save">Save 20%</span>
                    </button>
                </div>
            </div>

            {/* Plans */}
            <div className="tp-plans-section">
                <div className="tp-plans-grid">
                    {PLANS.map((plan) => {
                        const price = billing === "Monthly" ? plan.priceMonthly : plan.priceYearly;
                        const cbItem = CB_ITEMS[plan.name][billing];
                        const isCurrent = currentPlan === cbItem;
                        const isSameTierDifferentBilling = !isCurrent && currentPlanName === plan.name;
                        return (
                        <div
                            key={plan.name}
                            className={`tp-plan-card${plan.featured && !isCurrent ? " tp-plan-card--featured" : ""}${isCurrent ? " tp-plan-card--current" : ""}`}
                        >
                            {isCurrent && <span className="tp-current-badge">Current Plan</span>}
                            {!isCurrent && plan.featured && <span className="tp-popular-badge">Most Popular</span>}

                            <p className="tp-plan-name">{plan.name}</p>
                            <p className="tp-plan-desc">{plan.desc}</p>

                            <div className="tp-plan-price">
                                <span className="tp-plan-price-currency">$</span>
                                <span className="tp-plan-price-amount">{price}</span>
                                <span className="tp-plan-price-period">/mo</span>
                                {billing === "Yearly" && (
                                    <span className="tp-plan-price-strikethrough">${plan.priceMonthly}</span>
                                )}
                            </div>
                            {billing === "Yearly" && (
                                <p className="tp-plan-billed-note">Billed annually</p>
                            )}

                            <div className="tp-plan-limits">
                                <div className="tp-plan-limit-row">
                                    <span className="tp-plan-limit-label">Users</span>
                                    <span className="tp-plan-limit-value">{plan.users}</span>
                                </div>
                                <div className="tp-plan-limit-row">
                                    <span className="tp-plan-limit-label">Messages</span>
                                    <span className="tp-plan-limit-value">{plan.messages}</span>
                                </div>
                                <div className="tp-plan-limit-row">
                                    <Tooltip title={CONNECTION_TOOLTIP} placement="bottom">
                                        <span className="tp-plan-limit-label tp-connection-label">
                                            Connections <span className="tp-info-icon">ⓘ</span>
                                        </span>
                                    </Tooltip>
                                    <span className="tp-plan-limit-value">{plan.connections}</span>
                                </div>
                            </div>

                            {isCurrent ? (
                                <div className="tp-plan-cta-link tp-plan-cta-link--current">
                                    Current Plan
                                </div>
                            ) : isSameTierDifferentBilling ? (
                                <button
                                    key={cbItem}
                                    className="tp-plan-cta-link tp-plan-cta-link--outline"
                                    onClick={() => { setSwitchingToPlan(cbItem); setShowSwitchConfirmModal(true); }}
                                >
                                    {billing === "Yearly" ? "Switch to Yearly" : "Switch to Monthly"}
                                </button>
                            ) : PLAN_TIER[plan.name] < currentPlanTier ? (
                                <button
                                    className="tp-plan-cta-link tp-plan-cta-link--outline"
                                    onClick={() => setShowDowngradeModal(true)}
                                >
                                    Downgrade
                                </button>
                            ) : currentPlanTier >= 0 ? (
                                <button
                                    key={cbItem}
                                    className={`tp-plan-cta-link${plan.featured ? " tp-plan-cta-link--primary" : " tp-plan-cta-link--outline"}`}
                                    onClick={() => { setSwitchingToPlan(cbItem); setShowSwitchConfirmModal(true); }}
                                >
                                    Upgrade
                                </button>
                            ) : (
                                <a
                                    key={cbItem}
                                    href="#"
                                    data-cb-type="checkout"
                                    data-cb-item-0={cbItem}
                                    onMouseEnter={handleSubscribeClick}
                                    className={`tp-plan-cta-link${plan.featured ? " tp-plan-cta-link--primary" : " tp-plan-cta-link--outline"}`}
                                >
                                    Subscribe
                                </a>
                            )}

                            <p className="tp-plan-features-title">What's included</p>
                            <div className="d-flex flex-column">
                                {plan.features.map((feature, index) => (
                                    <div key={index} className="tp-plan-feature-item">
                                        <span className="tp-feature-check">✓</span>
                                        <span>{feature}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                    })}
                </div>
            </div>

            {/* Add-ons */}
            <div className="tp-addons-section">
                <h2 className="tp-addons-title">Add-ons</h2>
                <div className="tp-addons-grid">
                    <div className="tp-addon-card">
                        <span className="tp-addon-icon">🔗</span>
                        <p className="tp-addon-name">Additional Connection</p>
                        <p className="tp-addon-price">$15 / connection</p>
                        <p className="tp-addon-desc">
                            Need more messaging channels? Add extra connections to your plan at any time. Each connection
                            links an additional phone number or messaging service.
                        </p>
                    </div>
                    <div className="tp-addon-card">
                        <span className="tp-addon-icon">✨</span>
                        <p className="tp-addon-name">AI Addon</p>
                        <p className="tp-addon-price">From $25</p>
                        <p className="tp-addon-desc">
                            Unlock AI-powered features with a credit-based system. Buy credits starting at $25 — credits
                            roll over month to month and never expire. Usage scales with the features you use.
                        </p>
                    </div>
                </div>
            </div>

            {/* Feature comparison */}
            <div className="tp-comparison-section">
                <div className="tp-comparison-inner">
                    <h2 className="tp-comparison-title">See what's included in each plan</h2>
                    <table className="tp-comparison-table">
                        <thead>
                            <tr>
                                <th>Feature</th>
                                <th>Starter</th>
                                <th>Growth</th>
                                <th>Business</th>
                            </tr>
                        </thead>
                        <tbody>
                            {COMPARISON_ROWS.map((row, index) => (
                                <tr key={index}>
                                    <td>{row.feature}</td>
                                    <td>
                                        <CheckIcon value={row.starter} />
                                    </td>
                                    <td>
                                        <CheckIcon value={row.growth} />
                                    </td>
                                    <td>
                                        <CheckIcon value={row.business} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* FAQ */}
            <div className="tp-faq-section">
                <div className="tp-faq-inner">
                    <h2 className="tp-faq-title">Frequently Asked Questions</h2>
                    {getFaqs(serviceName).map((faq) => (
                        <FaqItem
                            key={faq.key}
                            faq={faq}
                            isExpanded={expandedFaqKey === faq.key}
                            onToggle={() => handleToggleFaq(faq.key)}
                        />
                    ))}
                </div>
            </div>

            {/* Footer */}
            <div className="tp-footer">
                Questions? Email us at{" "}
                <a href={`mailto:${ASSIST_MAIL}`} style={{ color: "#605bff" }}>
                    {ASSIST_MAIL}
                </a>
                &nbsp;·&nbsp; HelloSend &copy; {new Date().getFullYear()}
            </div>

            <Modal
                centered
                width={425}
                open={showSwitchConfirmModal}
                footer={null}
                closable={false}
                className="successPricingModel"
            >
                <div className="mt-4 mb-2">
                    <div className="d-flex justify-content-center w-100 mb-2">
                        <span className="hs-fs-16 hs-fw-bolder hs-color-BlackRussian">Confirm Plan Change</span>
                    </div>
                    <div className="d-flex justify-content-center">
                        <span className="hs-fs-14">The unused portion of your current plan will be credited back and applied toward your new plan — you only pay the difference for the remaining period.</span>
                    </div>
                </div>
                <div className="d-flex justify-content-center gap-2 mt-3">
                    <button
                        className="tp-plan-cta-link tp-plan-cta-link--primary hs-w-100"
                        onClick={handleSwitchPlan}
                        disabled={spinButton}
                    >
                        {spinButton ? "Processing..." : "Confirm"}
                    </button>
                    <button
                        className="tp-plan-cta-link tp-plan-cta-link--outline hs-w-100"
                        onClick={() => { setShowSwitchConfirmModal(false); setSwitchingToPlan(null); }}
                        disabled={spinButton}
                    >
                        Cancel
                    </button>
                </div>
            </Modal>

            <Modal
                centered
                width={425}
                open={showDowngradeModal}
                footer={null}
                closable={false}
                className="successPricingModel"
            >
                <div className="mt-4 mb-2">
                    <div className="d-flex justify-content-center w-100 mb-2">
                        <span className="hs-fs-16 hs-fw-bolder hs-color-BlackRussian">Downgrade Plan</span>
                    </div>
                    <div className="d-flex justify-content-center">
                        <span className="hs-fs-14">To downgrade your plan, please contact our support team and we'll help you through the process.</span>
                    </div>
                </div>
                <div className="d-flex justify-content-center gap-2 mt-3">
                    <button className="tp-plan-cta-link tp-plan-cta-link--primary hs-w-100" onClick={() => { window.open(`mailto:${ASSIST_MAIL}`, "_self"); setShowDowngradeModal(false); }}>
                        Contact Support
                    </button>
                    <button className="tp-plan-cta-link tp-plan-cta-link--outline hs-w-100" onClick={() => setShowDowngradeModal(false)}>
                        Cancel
                    </button>
                </div>
            </Modal>

            <Modal
                centered
                width={425}
                open={checkoutStatus !== null}
                footer={null}
                closable={false}
                className="successPricingModel"
            >
                {checkoutStatus === "success" ? (
                    <>
                        <div className="mt-4 mb-2">
                            <div className="d-flex justify-content-center w-100 mb-2">
                                <span className="hs-fs-16 hs-fw-bolder hs-color-BlackRussian">You're All Set!</span>
                            </div>
                            <div className="d-flex justify-content-center">
                                <span className="hs-fs-14">Your subscription is now active and will appear in our system shortly.</span>
                            </div>
                        </div>
                        <div className="d-flex justify-content-center mt-3">
                            <button className="tp-plan-cta-link tp-plan-cta-link--primary hs-w-100" onClick={handleGoHome}>
                                Done
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="mt-4 mb-2">
                            <div className="d-flex justify-content-center w-100 mb-2">
                                <span className="hs-fs-16 hs-fw-bolder hs-color-BlackRussian">Something went wrong</span>
                            </div>
                            <div className="d-flex justify-content-center">
                                <span className="hs-fs-14">We couldn't complete your subscription. Please try again or contact support.</span>
                            </div>
                        </div>
                        <div className="d-flex justify-content-center mt-3">
                            <button className="tp-plan-cta-link tp-plan-cta-link--outline hs-w-100" onClick={() => setCheckoutStatus(null)}>
                                Close
                            </button>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
}
